import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GenericTextField from '../../../components/GenericTextField/GenericTextField';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import MoneyField from '../../../components/MoneyField';
import GenericInfoModal from '../../../components/GenericInfoModal';
import { CommonModalContentBox, CommonModalWrapperBox } from '../../../common/styles/Common.styles';
import { MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { createNotification } from '../../../utilities/helper/notification.helper';
import { formatMoney } from '../../../utilities/helper/money.helper';
import { toLocalDateString } from '../../../utilities/helper/date.helper';
import { PAYMENT_METHOD_LABELS, PaymentMethodEnum } from '../../../common/enums/invoicing.enum';
import { PaymentDto } from '../../../common/interfaces/invoicing.interface';
import { getChildPayments, recordPayment, removePayment } from '../../../api/invoicing.api';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../theme';
interface ChildPaymentsModalProps {
  kindergartenId: string;
  childId: string;
  childName: string;
  outstanding: number;
  onClose: () => void;
  onChanged: () => void;
}

/**
 * Money received for one child, and what it settled.
 *
 * A payment is recorded against the child rather than against an invoice, because that
 * is how parents pay - a round sum, late, or two months at once. The allocation is
 * shown afterwards so the admin can see where it went without having to work it out.
 */
const ChildPaymentsModal: React.FC<ChildPaymentsModalProps> = ({ kindergartenId, childId, childName, outstanding, onClose, onChanged }) => {
  const { t } = useTranslation();
  const [payments, setPayments] = useState<PaymentDto[]>([]);
  const [loader, setLoader] = useState(true);
  const [amount, setAmount] = useState(outstanding > 0 ? outstanding : 0);
  const [paidAt, setPaidAt] = useState(toLocalDateString(new Date()));
  const [method, setMethod] = useState<PaymentMethodEnum>(PaymentMethodEnum.BANK_TRANSFER);
  const [paymentReference, setPaymentReference] = useState('');
  const [toRemove, setToRemove] = useState<PaymentDto | null>(null);

  const load = useCallback(async () => {
    setLoader(true);
    try {
      const result = await getChildPayments(kindergartenId, childId);
      setPayments(result.results ?? []);
    } catch {
      createNotification(MESSAGE_TYPE.ERROR, t('payments.loadFailed'));
    } finally {
      setLoader(false);
    }
  }, [kindergartenId, childId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRecord = async (): Promise<void> => {
    if (!amount || amount <= 0) {
      createNotification(MESSAGE_TYPE.ERROR, t('payments.validation.amountRequired'));
      return;
    }
    try {
      await recordPayment(kindergartenId, childId, {
        amount,
        paidAt,
        method,
        paymentReference: paymentReference.trim() || undefined
      });
      createNotification(MESSAGE_TYPE.SUCCESS, t('payments.recorded'));
      setPaymentReference('');
      await load();
      onChanged();
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('payments.recordFailed'));
    }
  };

  const handleRemove = async () => {
    if (!toRemove) return;
    try {
      await removePayment(kindergartenId, toRemove.id);
      createNotification(MESSAGE_TYPE.SUCCESS, t('payments.removed'));
      setToRemove(null);
      await load();
      onChanged();
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('payments.removeFailed'));
    }
  };

  return (
    <Modal open onClose={onClose}>
      <CommonModalWrapperBox sx={{ width: 860, maxHeight: '90vh', overflowY: 'auto' }}>
        <CommonModalContentBox>
          <Typography variant="h2" sx={{ fontSize: 22, mb: 1 }}>
            {t('payments.modalTitle', { name: childName })}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 3 }}>
            {t('payments.allocationExplanation')}
          </Typography>

          {outstanding > 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              {t('payments.outstandingLabel')} <strong>{formatMoney(outstanding, { withCurrency: true })}</strong>
            </Alert>
          )}

          <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 4, p: 2, mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <MoneyField label={t('payments.columns.amount')} value={amount} onChange={value => setAmount(value ?? 0)} />
              <GenericTextField label={t('payments.receivedOn')} isDate value={paidAt} onChange={e => setPaidAt(e.target.value)} />
              <FormControl fullWidth>
                <InputLabel id="payment-method-label">{t('payments.columns.method')}</InputLabel>
                <Select
                  labelId="payment-method-label"
                  label={t('payments.columns.method')}
                  value={method}
                  onChange={e => setMethod(e.target.value as PaymentMethodEnum)}
                  sx={{ borderRadius: '10px', backgroundColor: palette.surface.main }}
                >
                  {Object.values(PaymentMethodEnum).map(value => (
                    <MenuItem key={value} value={value}>
                      {t(PAYMENT_METHOD_LABELS[value])}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <GenericTextField
                label={t('payments.reference')}
                value={paymentReference}
                onChange={e => setPaymentReference(e.target.value)}
                helperText={t('payments.referenceHelp')}
              />
              <GenericButton buttonType={GenericButtonTypeEnum.Primary} variant="contained" onAsyncAction={handleRecord} label={t('payments.recordShort')} />
            </Stack>
          </Box>

          {loader ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !payments.length ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('payments.noneForChild')}
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '14%' }}>{t('payments.columns.received')}</TableCell>
                  <TableCell sx={{ width: '14%', textAlign: 'right' }}>{t('payments.columns.amount')}</TableCell>
                  <TableCell sx={{ width: '14%' }}>{t('payments.columns.method')}</TableCell>
                  <TableCell>{t('payments.columns.settled')}</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map(payment => (
                  <TableRow key={payment.id}>
                    <TableCell>{payment.paidAt}</TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>{formatMoney(payment.amount)}</TableCell>
                    <TableCell>{t(PAYMENT_METHOD_LABELS[payment.method])}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {payment.allocations.map(allocation => (
                        <Chip
                          key={allocation.invoiceId}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5 }}
                          label={`${allocation.invoiceNumber ?? t('invoices.draftLower')}: ${formatMoney(allocation.amount)}`}
                        />
                      ))}
                      {payment.unallocatedAmount > 0 && (
                        <Chip size="small" color="info" variant="outlined" label={t('billing.creditAmount', { amount: formatMoney(payment.unallocatedAmount) })} />
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => setToRemove(payment)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="outlined" onClick={onClose} label={t('common.close')} />
          </Stack>

          {/* money leaving the record deserves the same confirmation a draft gets;
              removing a payment also re-opens whatever it had settled */}
          {toRemove && (
            <GenericInfoModal
              open
              onClose={() => setToRemove(null)}
              onConfirm={handleRemove}
              label={t('payments.remove')}
              confirmLabel={t('common.remove')}
            >
              <Alert severity="warning" sx={{ borderRadius: 2, mt: 1 }}>
                {t('payments.removeWarning', { amount: formatMoney(toRemove.amount, { withCurrency: true }), date: toRemove.paidAt })}
              </Alert>
            </GenericInfoModal>
          )}
        </CommonModalContentBox>
      </CommonModalWrapperBox>
    </Modal>
  );
};

export default ChildPaymentsModal;
